import type { CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { DatePicker, type DatePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import { DropDownList, type DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { GridColumn, GridNoRecords, type GridCellProps, type GridDataStateChangeEvent, type GridFilterChangeEvent, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useListKnowledgeInsightsQuery } from "../../api/admin/adminKnowledgeInsightApi";
import { useListProductsQuery } from "../../api/admin/adminProductApi";
import { useListAdminRetailersQuery } from "../../api/admin/adminRetailerApi";
import { useListAdminSubBrandsQuery } from "../../api/admin/adminSubBrandApi";
import AppGrid from "../../components/grid/appGrid";
import type { CustomerDto, KnowledgeInsightDto, ListAdminSubBrandsRequest, ProductDto, RetailerDto, SubBrandDto } from "../../contracts";
import { formatDate, formatDateTime, toDate } from "../../utils/dateValueConversion";

const initialSort: SortDescriptor[] = [{ field: "createdAt", dir: "desc" }];
const selectorRequest: DataSourceRequestState = { skip: 0, take: 1000, sort: [{ field: "name", dir: "asc" }] };

const buildFilter = (
    selectedCustomer: CustomerDto | null,
    selectedProduct: ProductDto | null,
    selectedSubBrand: SubBrandDto | null,
    selectedRetailer: RetailerDto | null,
    startDate: Date | null,
    endDate: Date | null,
    dataStateFilter?: CompositeFilterDescriptor
): CompositeFilterDescriptor | undefined => {
    const filters: Array<CompositeFilterDescriptor | FilterDescriptor> = [
        ...(dataStateFilter == null ? [] : [dataStateFilter]),
        ...(selectedCustomer == null ? [] : [{ field: "customerId", operator: "eq", value: selectedCustomer.id }]),
        ...(selectedRetailer == null ? [] : [{ field: "retailerId", operator: "eq", value: selectedRetailer.id }]),
        ...(startDate == null ? [] : [{ field: "endDate", operator: "gte", value: startDate }]),
        ...(endDate == null ? [] : [{ field: "startDate", operator: "lte", value: endDate }])
    ];

    if (selectedProduct != null) {
        filters.push({
            logic: "or",
            filters: [
                { field: "productId", operator: "eq", value: selectedProduct.id },
                ...(selectedProduct.sub_brand == null
                    ? []
                    : [
                            { field: "subBrand.name", operator: "eq", value: selectedProduct.sub_brand },
                            { field: "product.subBrand.name", operator: "eq", value: selectedProduct.sub_brand }
                        ])
            ]
        });
    }

    if (selectedSubBrand != null) {
        filters.push({
            logic: "or",
            filters: [
                { field: "subBrandId", operator: "eq", value: selectedSubBrand.id },
                { field: "product.subBrandId", operator: "eq", value: selectedSubBrand.id }
            ]
        });
    }

    return filters.length === 0 ? undefined : { logic: "and", filters };
};

const DateCell = (includeTime: boolean): React.FC<GridCellProps> => props => {
    const value = props.field == null ? null : (props.dataItem as Record<string, Date | string | null>)[props.field];
    const date = toDate(value);
    return <td className="k-table-td text-nowrap">{date == null ? "-" : includeTime ? formatDateTime(date) : formatDate(date)}</td>;
};

const CustomerCell = (props: GridCellProps): React.ReactElement => {
    const insight = props.dataItem as KnowledgeInsightDto;
    return <td className="k-table-td">{`${insight.customer_name} (#${insight.customer_id})`}</td>;
};

const AuthorCell = (props: GridCellProps): React.ReactElement => {
    const insight = props.dataItem as KnowledgeInsightDto;
    return <td className="k-table-td">{insight.author_type}</td>;
};

const TargetTypeCell = (props: GridCellProps): React.ReactElement => {
    const insight = props.dataItem as KnowledgeInsightDto;
    return <td className="k-table-td">{insight.product_id != null ? "SKU" : insight.sub_brand_id != null ? "Sub-brand" : "General"}</td>;
};

const TargetCell = (props: GridCellProps): React.ReactElement => {
    const insight = props.dataItem as KnowledgeInsightDto;
    return <td className="k-table-td">{insight.product_name ?? insight.sub_brand_name ?? "General"}</td>;
};

const RetailerCell = (props: GridCellProps): React.ReactElement => {
    const insight = props.dataItem as KnowledgeInsightDto;
    return <td className="k-table-td">{insight.retailer_name ?? "All retailers"}</td>;
};

const ContentCell = (props: GridCellProps): React.ReactElement => {
    const insight = props.dataItem as KnowledgeInsightDto;
    return (
        <td className="k-table-td">
            <div className="text-wrap text-break" style={{ minWidth: "280px", whiteSpace: "pre-wrap" }}>
                {insight.content}
            </div>
        </td>
    );
};

const KnowledgeInsightManagement: React.FC = () => {
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(null);
    const [selectedSubBrand, setSelectedSubBrand] = useState<SubBrandDto | null>(null);
    const [selectedRetailer, setSelectedRetailer] = useState<RetailerDto | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 50, sort: initialSort });
    const listRequest: DataSourceRequestState = {
        ...dataState,
        filter: buildFilter(selectedCustomer, selectedProduct, selectedSubBrand, selectedRetailer, startDate, endDate, dataState.filter)
    };
    const targetRequest = selectedCustomer == null
        ? skipToken
        : {
                ...selectorRequest,
                filter: { logic: "and" as const, filters: [{ field: "retailer.customerId", operator: "eq", value: selectedCustomer.id }] }
            };
    const retailerRequest = selectedCustomer == null
        ? skipToken
        : {
                ...selectorRequest,
                filter: { logic: "and" as const, filters: [{ field: "customerId", operator: "eq", value: selectedCustomer.id }] }
            };
    const subBrandRequest = selectedCustomer == null ? skipToken : { customer_id: selectedCustomer.id } as ListAdminSubBrandsRequest;

    const { data, isFetching, isError } = useListKnowledgeInsightsQuery(listRequest);
    const { data: customerData, isFetching: isCustomersFetching, isError: isCustomersError } = useListCustomersQuery(selectorRequest);
    const { currentData: productData, isFetching: isProductsFetching, isError: isProductsError } = useListProductsQuery(targetRequest);
    const { currentData: retailerData, isFetching: isRetailersFetching, isError: isRetailersError } = useListAdminRetailersQuery(retailerRequest);
    const { currentData: subBrandData, isFetching: isSubBrandsFetching, isError: isSubBrandsError } = useListAdminSubBrandsQuery(subBrandRequest);

    const resetPaging = (): void => setDataState(previous => ({ ...previous, skip: 0 }));
    const handleCustomerChange = (event: DropDownListChangeEvent): void => {
        const customer = event.value as CustomerDto | null;
        setSelectedCustomer(customer?.id === 0 ? null : customer);
        setSelectedProduct(null);
        setSelectedSubBrand(null);
        setSelectedRetailer(null);
        resetPaging();
    };
    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>) => (event: DatePickerChangeEvent): void => {
        setter(event.value);
        resetPaging();
    };
    const clearFilters = (): void => {
        setSelectedCustomer(null);
        setSelectedProduct(null);
        setSelectedSubBrand(null);
        setSelectedRetailer(null);
        setStartDate(null);
        setEndDate(null);
        resetPaging();
    };

    const pageChange = (event: GridPageChangeEvent): void => setDataState(previous => ({
        ...previous,
        skip: event.page.skip,
        take: event.page.take
    }));
    const dataStateChange = (event: GridDataStateChangeEvent): void => setDataState({
        ...event.dataState,
        skip: event.dataState.skip,
        take: event.dataState.take
    });
    const filterChange = (event: GridFilterChangeEvent): void => setDataState(previous => ({ ...previous, filter: event.filter, skip: 0 }));
    const sortChange = (event: GridSortChangeEvent): void => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }));

    const insights = (data?.data ?? []).map(insight => ({
        ...insight,
        createdAt: toDate(insight.created_at),
        startDate: toDate(insight.start_date),
        endDate: toDate(insight.end_date)
    }));

    return (
        <div className="d-flex flex-column gap-3 p-3 h-100 min-h-0 overflow-hidden">
            <div className="row g-2 align-items-end">
                <div className="col-12 col-md-3 col-xl-2">
                    <label className="form-label fw-semibold" htmlFor="admin-knowledge-insight-customer">Customer</label>
                    <DropDownList id="admin-knowledge-insight-customer" data={customerData?.data ?? []} textField="name" dataItemKey="id" value={selectedCustomer} onChange={handleCustomerChange} disabled={isCustomersFetching} defaultItem={{ id: 0, name: isCustomersFetching ? "Loading customers..." : "All customers" }} />
                </div>
                <div className="col-12 col-md-3 col-xl-2">
                    <label className="form-label fw-semibold" htmlFor="admin-knowledge-insight-product">Product</label>
                    <DropDownList
                        id="admin-knowledge-insight-product"
                        data={productData?.data ?? []}
                        textField="name"
                        dataItemKey="id"
                        value={selectedProduct}
                        onChange={event => {
                            const product = event.value as ProductDto | null;
                            setSelectedProduct(product?.id === 0 ? null : product);
                            resetPaging();
                        }}
                        disabled={selectedCustomer == null || isProductsFetching}
                        defaultItem={{ id: 0, name: isProductsFetching ? "Loading products..." : "All products" }}
                    />
                </div>
                <div className="col-12 col-md-3 col-xl-2">
                    <label className="form-label fw-semibold" htmlFor="admin-knowledge-insight-sub-brand">Sub-brand</label>
                    <DropDownList
                        id="admin-knowledge-insight-sub-brand"
                        data={subBrandData?.items ?? []}
                        textField="name"
                        dataItemKey="id"
                        value={selectedSubBrand}
                        onChange={event => {
                            const subBrand = event.value as SubBrandDto | null;
                            setSelectedSubBrand(subBrand?.id === 0 ? null : subBrand);
                            resetPaging();
                        }}
                        disabled={selectedCustomer == null || isSubBrandsFetching}
                        defaultItem={{ id: 0, name: isSubBrandsFetching ? "Loading sub-brands..." : "All sub-brands" }}
                    />
                </div>
                <div className="col-12 col-md-3 col-xl-2">
                    <label className="form-label fw-semibold" htmlFor="admin-knowledge-insight-retailer">Retailer</label>
                    <DropDownList
                        id="admin-knowledge-insight-retailer"
                        data={retailerData?.data ?? []}
                        textField="name"
                        dataItemKey="id"
                        value={selectedRetailer}
                        onChange={event => {
                            const retailer = event.value as RetailerDto | null;
                            setSelectedRetailer(retailer?.id === 0 ? null : retailer);
                            resetPaging();
                        }}
                        disabled={selectedCustomer == null || isRetailersFetching}
                        defaultItem={{ id: 0, name: isRetailersFetching ? "Loading retailers..." : "All retailers" }}
                    />
                </div>
                <div className="col-6 col-md-3 col-xl-1">
                    <label className="form-label fw-semibold" htmlFor="admin-knowledge-insight-start-date">Start date</label>
                    <DatePicker id="admin-knowledge-insight-start-date" value={startDate} onChange={handleDateChange(setStartDate)} />
                </div>
                <div className="col-6 col-md-3 col-xl-1">
                    <label className="form-label fw-semibold" htmlFor="admin-knowledge-insight-end-date">End date</label>
                    <DatePicker id="admin-knowledge-insight-end-date" value={endDate} onChange={handleDateChange(setEndDate)} />
                </div>
                <div className="col-12 col-md-auto">
                    <Button type="button" themeColor="secondary" fillMode="outline" onClick={clearFilters}>Clear filters</Button>
                </div>
            </div>
            {(isError || isCustomersError || isProductsError || isSubBrandsError || isRetailersError) && <div className="alert alert-danger mb-0" role="alert">Unable to load knowledge insights or filter options. Please try again.</div>}
            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    showLoader={isFetching || isCustomersFetching || isProductsFetching || isSubBrandsFetching || isRetailersFetching}
                    dataItemKey="id"
                    scrollable="scrollable"
                    sortable={true}
                    data={insights}
                    resizable={true}
                    filterable={false}
                    initialState={dataState}
                    onPageChange={pageChange}
                    onDataStateChange={dataStateChange}
                    filter={dataState.filter}
                    sort={dataState.sort}
                    total={data?.total ?? 0}
                    skip={dataState.skip}
                    take={dataState.take}
                    defaultSort={initialSort}
                    onFilterChange={filterChange}
                    onSortChange={sortChange}
                    pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                    style={{ height: "100%" }}
                >
                    <GridNoRecords>{!isFetching && <span className="text-muted">{isError ? "Unable to load knowledge insights. Please try again." : "No knowledge insights found."}</span>}</GridNoRecords>
                    <GridColumn field="id" title="ID" width="100px" />
                    <GridColumn field="customerName" title="Customer" cells={{ data: CustomerCell }} width="220px" />
                    <GridColumn field="authorType" title="Author" cells={{ data: AuthorCell }} width="120px" />
                    <GridColumn title="Target type" cells={{ data: TargetTypeCell }} filterable={false} width="130px" />
                    <GridColumn title="Target" cells={{ data: TargetCell }} filterable={false} width="220px" />
                    <GridColumn field="retailerName" title="Retailer" cells={{ data: RetailerCell }} width="180px" />
                    <GridColumn field="key" title="Key" width="180px" />
                    <GridColumn field="startDate" title="Insight start" cells={{ data: DateCell(false) }} width="160px" />
                    <GridColumn field="endDate" title="Insight end" cells={{ data: DateCell(false) }} width="160px" />
                    <GridColumn field="createdAt" title="Created" cells={{ data: DateCell(true) }} width="200px" />
                    <GridColumn field="content" title="Content" cells={{ data: ContentCell }} filterable={false} width="420px" />
                </AppGrid>
            </div>
        </div>
    );
};

export default KnowledgeInsightManagement;
